import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import axios from "axios";
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import { type DataTableColumn } from "@routes-plan/ui";
import ClientHistoryModal from "./modals/ClientHistoryModal";
import { useSelection } from "../../components/context/SelectionContext";
import { Office } from "../../models/office";
import { Route } from "../../models/route";
import ClientQuoteModal from "../../components/common/ClientQuoteModal";
import { useResourcePermissions } from "../../hooks/useResourcePermissions";
import { isSuperAdminUser } from "../../utils/authScope";
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { formatNumber } from '../../utils/format/currency';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportSelect, ReportTable, RowAction } from "./components/ReportLayout";

type ClientRow = Client & { routeName: string; officeName: string };

// Tipos anidados en la respuesta de cliente (API)
interface ClientOfficeRef {
  id: number;
  name: string;
  phone: string;
  address: string;
  city: string;
  country: {
    id: number;
    name: string;
  };
}

interface ClientRouteRef {
  id: number;
  name: string;
  office: ClientOfficeRef;
}

interface Client {
  id: number;
  idNumber: string;
  fullName: string;
  backupAlertActive?: boolean;
  backups?: Array<{ id: number }>;
  creditLimit: number;
  phone: string;
  mobilePhone: string;
  mainAddress: string;
  secondaryAddress: string;
  job: string;
  coSignerFullName: string;
  coSignerIdNumber: string;
  coSignerAddress: string;
  coSignerPhone: string;
  route: ClientRouteRef;
}

interface ClientResponse {
  success: boolean;
  data: Client[];
  total: number;
}

interface ClientesHistProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Componente principal
const ClientHistory: React.FC<ClientesHistProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  setPageTitle("menu.reports.client-history");
  const [cedula, setCedula] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedClientForQuote, setSelectedClientForQuote] = useState<Client | null>(null);
  const [authUser, setAuthUser] = useState<{ isSystem?: number; username?: string } | null>(null);
  const [queried, setQueried] = useState(false);
  const { office_id, collect_id, setOffice_id, setCollect_id } = useSelection();
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const { canRead, canUpdateQuote, canManageBackupAlert } = useResourcePermissions('/reports/client-history');
  const isSuperAdmin = isSuperAdminUser(authUser);

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const [userResponse, officesResponse] = await Promise.all([
          axios.get("user"),
          axios.get("offices"),
        ]);
        setAuthUser(userResponse?.data?.data ?? null);
        const list = Array.isArray(officesResponse?.data?.data) ? officesResponse.data.data : [];
        setOffices(list);
      } catch (err) {
        console.error("Error cargando oficinas:", err);
      }
    };
    void loadOffices();
  }, []);

  useEffect(() => {
    if (offices.length === 0) return;
    setOffice_id((prev) =>
      prev && !offices.some((o) => String(o.id) === String(prev)) ? "" : prev
    );
  }, [offices, setOffice_id]);

  useEffect(() => {
    if (!office_id) {
      setCollectsOffice([]);
      return;
    }
    let cancelled = false;
    const loadCollects = async () => {
      try {
        const { data } = await axios.get(`offices/${office_id}/routes`);
        const list = Array.isArray(data) ? data : [];
        if (cancelled) return;
        setCollectsOffice(list);
        setCollect_id((prev) =>
          prev && !list.some((c) => String(c.id) === String(prev)) ? "" : prev
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Error cargando cobros:", err);
          setCollectsOffice([]);
        }
      }
    };
    void loadCollects();
    return () => {
      cancelled = true;
    };
  }, [office_id, setCollect_id]);

  useEffect(() => {
    setClients([]);
  }, [office_id, collect_id]);

  const performSearch = async () => {
    const trimmedSearch = cedula.trim();

    if (!trimmedSearch) {
      showAlert(t("clientReports.searchRequired"), "warning");
      return;
    }
    setQueried(true);

    if (isSuperAdmin) {
      setLoading(true);
      try {
        const response = await axios.post<ClientResponse>("/clients/search", {
          search: trimmedSearch,
        });

        if (response.data.success && response.data.data.length > 0) {
          setClients(response.data.data);
          showAlert(t("clientReports.clientsFoundAlert", { count: response.data.data.length }), "success");
        } else {
          setClients([]);
          showAlert(t("clientReports.noClientsFound"), "info");
        }
      } catch (error) {
        console.error("Error searching clients:", error);
        showAlert(
          t("clientReports.searchError"),
          "error"
        );
        setClients([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!String(office_id ?? "").trim() || !String(collect_id ?? "").trim()) {
      showAlert(t("clientReports.selectOfficeAndRouteFirst"), "warning");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post<ClientResponse>("/clients/search", {
        search: trimmedSearch,
        officeId: Number(office_id),
        routeId: Number(collect_id),
      });

      if (response.data.success && response.data.data.length > 0) {
        setClients(response.data.data);
        showAlert(t("clientReports.clientsFoundAlert", { count: response.data.data.length }), "success");
      } else {
        setClients([]);
        showAlert(t("clientReports.noClientsFound"), "info");
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      showAlert(
        t("clientReports.searchError"),
        "error"
      );
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (client: Client) => {
    setSelectedClient(client);
    setOpenModal(true);
  };

  // Preparar datos para la tabla
  const rows: ClientRow[] = [...clients]
    .sort((a, b) => {
      const officeCompare = String(a.route?.office?.name || "").localeCompare(
        String(b.route?.office?.name || ""),
        "es",
        { sensitivity: "base" }
      );
      if (officeCompare !== 0) return officeCompare;
      const routeCompare = String(a.route?.name || "").localeCompare(
        String(b.route?.name || ""),
        "es",
        { sensitivity: "base" }
      );
      if (routeCompare !== 0) return routeCompare;
      return String(a.fullName || "").localeCompare(String(b.fullName || ""), "es", {
        sensitivity: "base",
      });
    })
    .map((client) => ({
      ...client,
      routeName: client.route?.name ?? t("clientReports.notAvailable"),
      officeName: client.route?.office?.name ?? t("clientReports.notAvailable"),
    }));

  const columns: DataTableColumn<ClientRow>[] = [
    { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true },
    { id: 'fullName', header: t('clientReports.client'), value: (r) => r.fullName, sortable: true },
    { id: 'phone', header: t('common.phone'), value: (r) => r.phone, hideBelow: 'md' },
    { id: 'mobilePhone', header: t('common.cellphone'), value: (r) => r.mobilePhone, hideBelow: 'sm' },
    { id: 'creditLimit', header: t('clientReports.creditLimit'), value: (r) => Number(r.creditLimit ?? 0), render: (r) => formatNumber(r.creditLimit), align: 'right', sortable: true },
    { id: 'mainAddress', header: t('common.address'), value: (r) => r.mainAddress, wrap: true, hideBelow: 'md' },
    { id: 'office', header: t('clientReports.office'), value: (r) => r.officeName, sortable: true },
    { id: 'routeName', header: t('summaryReports.shared.route'), value: (r) => r.routeName, sortable: true },
  ];

  const emptyTitle =
    !isSuperAdmin && (!office_id || !collect_id)
      ? t("clientReports.selectToViewHistory")
      : !queried
        ? t("clientReports.enterSearch")
        : isSuperAdmin
          ? t("clientReports.noClientsFound")
          : t("clientReports.noClientsInRoute");

  return (
    <ReportPage
      title={t("clientReports.historyTitle")}
      description={isSuperAdmin ? t("clientReports.hintGlobalSearch") : t("clientReports.hintSelectThenSearch")}
    >
      <ReportFilters
        onSubmit={() => void performSearch()}
        submitDisabled={loading}
        submitLabel={loading ? t("clientReports.searching") : t("common.search")}
        submitButtonProps={{ "data-tour": "client-history-query" }}
      >
        {!isSuperAdmin && (
          <>
            <OfficeSelect offices={offices} value={office_id} onChange={(id) => setOffice_id(id)} required tourId="client-history-office" />
            <ReportSelect
              label={t("summaryReports.shared.route")}
              tourId="client-history-route"
              options={collectsOffice}
              value={collect_id}
              onChange={(id) => setCollect_id(id)}
              disabled={!office_id}
              required
            />
          </>
        )}
        <TextField
          size="small"
          fullWidth
          label={t("clientReports.searchLabel")}
          inputProps={{ "data-tour": "client-search" }}
          value={cedula}
          onChange={(event) => setCedula(event.target.value)}
          disabled={loading}
          placeholder={t("clientReports.searchPlaceholder")}
          helperText={isSuperAdmin ? t("clientReports.globalSearchHelp") : undefined}
        />
      </ReportFilters>

      <ReportResults>
        <ReportTable<ClientRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          exportFileName={t("clientReports.historyFileName")}
          tourId="client-history"
          ariaLabel={t("clientReports.historyTitle")}
          emptyState={<ReportEmpty queried={queried} title={emptyTitle} description={queried ? undefined : ""} />}
          rowActions={(row) => (
            <>
              <span data-tour="client-history-open">
                <RowAction
                  allowed={canRead}
                  deniedTooltip={t("clientReports.noPermissionHistory")}
                  label={t("clientReports.history")}
                  icon={<HistoryOutlined fontSize="small" />}
                  onClick={() => handleOpenModal(row)}
                />
              </span>
              <RowAction
                allowed={canUpdateQuote}
                deniedTooltip={t("clientReports.noPermissionCreditLimit")}
                label={t("clientReports.editCreditLimit")}
                color="info"
                icon={<PersonOutline fontSize="small" />}
                onClick={() => {
                  setSelectedClientForQuote(row);
                  setQuoteModalOpen(true);
                }}
              />
            </>
          )}
        />
      </ReportResults>
      <ClientHistoryModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setClients((current) =>
            current.map((client) =>
              client.id === clientId
                ? { ...client, backupAlertActive: active }
                : client
            )
          );
          setSelectedClient((current) =>
            current && current.id === clientId
              ? { ...current, backupAlertActive: active }
              : current
          );
        }}
        clientData={selectedClient ? {
          id: selectedClient.id,
          cedula: selectedClient.idNumber?.toString() || "",
          nombre: selectedClient.fullName || "",
          direccion: selectedClient.mainAddress || "",
          telefono: selectedClient.phone || "",
          oficio: selectedClient.job || "",
          calificacion: "",
          celular: selectedClient.mobilePhone || "",
          routeId:
            selectedClient.route?.id ??
            (Number.isFinite(Number(collect_id)) && Number(collect_id) > 0
              ? Number(collect_id)
              : undefined),
          backupAlertActive: Boolean(selectedClient.backupAlertActive),
          backups: (selectedClient as any).backups ?? [],
        } : null}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ClientQuoteModal
        open={quoteModalOpen}
        client={selectedClientForQuote}
        onClose={() => {
          setQuoteModalOpen(false);
          setSelectedClientForQuote(null);
        }}
        onSaved={(nextQuote) => {
          setClients((current) =>
            current.map((client) =>
              client.id === selectedClientForQuote?.id
                ? { ...client, creditLimit: nextQuote }
                : client
            )
          );
        }}
        showAlert={showAlert}
      />
    </ReportPage>
  );
};

export default ClientHistory;
